import React from 'react';
import { FunctionComponent, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'src/hooks';
import { Card, DashboardWrapper, Footer, Text, AIDialog, PlatformTitle } from 'src/shared';
import { Size } from 'src/shared/text';
import { 
    ContentView, 
    ResponsiveContentContainer, 
    NetworkContainer, 
    BackButton, 
    HeaderTitle, 
    HeaderDescription, 
    GalleryHeader, 
    SearchContainer, 
    SearchInput, 
    SearchButton, 
    SearchHelpText,
    ActionButtonsContainer,
    UploadButton,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    UploadTabs,
    UploadTab,
    UploadTabContent,
    FileUploadArea,
    URLInput,
    LoginPrompt,
    FeatureLockedMessage
} from './styles';
import PlotlyNetworkVisualization from './PlotlyNetworkVisualization';
import { Col, Row } from 'react-bootstrap';
import { Colors } from 'src/utils/color';
import { getGenes, getPresignedPaperUploadURL, processPaperUpload, processPaperURL } from 'src/services/api';
import { getAuthToken } from 'src/utils/auth';
import { verboseLog, verboseWarn } from 'src/utils/config';

// Define interfaces for the data structures
interface GeneInfo {
    ensembl_id: string;
    gene_symbol?: string;
    gene_name?: string;
    chromosome?: string;
    gene_function?: string;
    pdb_ids: string[];
    associated_drugs: DrugInfo[];
}

interface DrugInfo {
    chembl_id: string;
    drug_name: string;
    company_name?: string;
    indication?: string;
    intended_use?: string;
    mechanism_of_action?: string;
    related_diseases: DiseaseInfo[];
    therapeutic_targets: TherapeuticTargetInfo[];
}

interface DiseaseInfo {
    disease_name: string;
    imaging_modalities: string[];
    medical_devices?: MedicalDeviceInfo[];
}

interface TherapeuticTargetInfo {
    target_name: string;
    interaction_type: string;
    binding_affinity: string;
    mechanism_of_action: string;
}

interface MedicalDeviceInfo {
    device_name: string;
    k_number: string;
    imaging_modality?: string;
}

interface UserTier {
    tier_name: string;
    tier_id: number;
}

type Props = Record<string, never>;

const Gene: FunctionComponent<Props> = ({}: Props) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isLoggedIn = Boolean(getAuthToken());
    const fileInputRef = useRef<HTMLInputElement>(null);

    // State management
    const [genesData, setGenesData] = useState<GeneInfo[]>([]);
    const [originalGenesData, setOriginalGenesData] = useState<GeneInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedGene, setSelectedGene] = useState<GeneInfo | null>(null);
    const [selectedPDBId, setSelectedPDBId] = useState<string | null>(null);
    const [showPDBViewer, setShowPDBViewer] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    
    // Paper upload state
    const [showPaperUploadModal, setShowPaperUploadModal] = useState<boolean>(false);
    const [uploadTab, setUploadTab] = useState<'file' | 'url'>('file');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [paperURL, setPaperURL] = useState<string>('');
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    
    // User tier state
    const [userTier, setUserTier] = useState<UserTier | null>(null);
    const [loadingUserTier, setLoadingUserTier] = useState<boolean>(false);

    // Load user tier on mount if logged in
    useEffect(() => {
        if (isLoggedIn) {
            loadUserTier();
        }
    }, [isLoggedIn]);

    // Load genes data on component mount
    useEffect(() => {
        loadGenesData();
    }, []);

    const loadUserTier = async () => {
        if (!isLoggedIn) return;
        
        try {
            setLoadingUserTier(true);
            // TODO: Implement API call to get user subscription info
            // For now, default to Free tier
            setUserTier({ tier_name: 'Free', tier_id: 1 });
        } catch (err) {
            console.error('Error loading user tier:', err);
            // Default to Free tier on error
            setUserTier({ tier_name: 'Free', tier_id: 1 });
        } finally {
            setLoadingUserTier(false);
        }
    };

    const loadGenesData = async (forceUpdate: boolean = false) => {
        try {
            setLoading(true);
            setError(null);
            
            verboseLog(`📡 LOADING: Starting loadGenesData with forceUpdate=${forceUpdate}`);
            
            const defaultGeneIds = ['ENSG00000142192'];
            
            const genePromises = defaultGeneIds.map(geneId => {
                verboseLog(`📡 API CALL: getGenes(1, [${geneId}], ${forceUpdate})`);
                return getGenes(1, [geneId], forceUpdate);
            });
            
            verboseLog('Making individual API calls for each gene...');
            const responses = await Promise.all(genePromises);
            
            verboseLog('All API Responses:', responses);
            
            const allGenes = [];
            for (const response of responses) {
                if (response && response.success && response.data && response.data.genes && response.data.genes.length > 0) {
                    allGenes.push(response.data.genes[0]);
                } else if (response && Array.isArray(response) && response.length > 0) {
                    allGenes.push(response[0]);
                } else {
                    verboseWarn('Unexpected response format for gene:', response);
                }
            }
            
            if (allGenes.length > 0) {
                setGenesData(allGenes);
                setOriginalGenesData(allGenes);
                verboseLog(`Successfully loaded ${allGenes.length} genes`);
            } else {
                console.error('No valid gene data received from any API call');
                setError('No valid gene data received from server');
            }
        } catch (err) {
            console.error('Error loading genes data:', err);
            setError('Failed to load genes data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        try {
            setIsRefreshing(true);
            setError(null);
            
            verboseLog('🔄 REFRESH: Starting refresh with forceUpdate=true');
            await loadGenesData(true);
            verboseLog('🔄 REFRESH: Refresh completed');
        } catch (err) {
            console.error('Error refreshing genes data:', err);
            setError('Failed to refresh genes data. Please try again.');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleGeneClick = (gene: GeneInfo) => {
        setSelectedGene(gene);
        if (gene.pdb_ids && gene.pdb_ids.length > 0) {
            setSelectedPDBId(gene.pdb_ids[0]);
            setShowPDBViewer(true);
        }
    };

    const handlePDBViewerClose = () => {
        setShowPDBViewer(false);
        setSelectedPDBId(null);
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        
        // Check if user is logged in for search feature
        if (!isLoggedIn) {
            setError('Please log in to search for genes.');
            return;
        }
        
        try {
            setIsSearching(true);
            setError(null);
            
            // Search within the original data
            const filteredGenes = originalGenesData.filter(gene => 
                gene.ensembl_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (gene.gene_symbol && gene.gene_symbol.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (gene.gene_name && gene.gene_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (gene.gene_function && gene.gene_function.toLowerCase().includes(searchQuery.toLowerCase()))
            );
            
            if (filteredGenes.length > 0) {
                setGenesData(filteredGenes);
            } else {
                setError(`No genes found matching "${searchQuery}". Try searching with a different term.`);
            }
        } catch (err) {
            console.error('Error searching genes:', err);
            setError('Search failed. Please try again.');
        } finally {
            setIsSearching(false);
        }
    };

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const resetSearch = () => {
        setSearchQuery('');
        setGenesData(originalGenesData);
        setError(null);
    };

    // Paper upload handlers
    const handleOpenPaperUpload = () => {
        if (!isLoggedIn) {
            setError('Please log in to upload scientific papers.');
            return;
        }
        setShowPaperUploadModal(true);
        setUploadError(null);
        setSelectedFile(null);
        setPaperURL('');
    };

    const handleClosePaperUpload = () => {
        setShowPaperUploadModal(false);
        setUploadError(null);
        setSelectedFile(null);
        setPaperURL('');
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                setUploadError('Please upload a PDF file.');
                return;
            }
            if (file.size > 15 * 1024 * 1024) { // 15MB limit
                setUploadError('File size must be less than 15MB.');
                return;
            }
            setSelectedFile(file);
            setUploadError(null);
        }
    };

    const handleURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPaperURL(e.target.value);
        setUploadError(null);
    };

    const handlePaperUpload = async () => {
        if (uploadTab === 'file' && !selectedFile) {
            setUploadError('Please select a PDF file to upload.');
            return;
        }
        
        if (uploadTab === 'url' && !paperURL.trim()) {
            setUploadError('Please enter a paper URL.');
            return;
        }

        if (uploadTab === 'url') {
            // Validate URL format
            try {
                new URL(paperURL);
            } catch {
                setUploadError('Please enter a valid URL.');
                return;
            }
        }

        try {
            setIsUploading(true);
            setUploadError(null);

            if (uploadTab === 'file' && selectedFile) {
                // Step 1: Get presigned URL for PDF upload
                const presignResponse = await getPresignedPaperUploadURL(selectedFile.name);
                if (!presignResponse.success || !presignResponse.presigned_url) {
                    throw new Error('Failed to get upload URL');
                }

                const { presigned_url, s3_key, document_uuid, s3_pdf_path } = presignResponse;

                // Step 2: Upload PDF to S3 using presigned URL
                const uploadResponse = await fetch(presigned_url, {
                    method: 'PUT',
                    body: selectedFile,
                    headers: {
                        'Content-Type': 'application/pdf',
                    },
                });

                if (!uploadResponse.ok) {
                    throw new Error('Failed to upload PDF to S3');
                }

                // Step 3: Notify backend to process the paper
                const processResponse = await processPaperUpload({
                    s3_pdf_path: s3_pdf_path,
                    document_uuid: document_uuid,
                });

                if (!processResponse.success) {
                    throw new Error(processResponse.message || 'Failed to process paper');
                }

                verboseLog('Paper uploaded and processed successfully:', processResponse);
                
            } else if (uploadTab === 'url') {
                // Process URL directly
                const processResponse = await processPaperURL({
                    paper_url: paperURL.trim(),
                });

                if (!processResponse.success) {
                    throw new Error(processResponse.message || 'Failed to process paper URL');
                }

                verboseLog('Paper URL processed successfully:', processResponse);
            }

            // After successful upload, close modal
            handleClosePaperUpload();
            
            // Show success message (you can add a toast notification here)
            setError(null); // Clear any previous errors
            
            // TODO: Update genesData with reasoning results from paper
            // This should trigger the graph visualization
            // For now, we'll just show a success state
            
        } catch (err: any) {
            console.error('Error uploading paper:', err);
            setUploadError(err.message || err.description || 'Failed to upload paper. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const renderNetworkVisualization = () => {
        if (!genesData || genesData.length === 0) return null;

        const nodes: any[] = [];
        const edges: any[] = [];
        const nodeIdMap = new Map<string, string>();
        
        const uniqueDrugs = new Set<string>();
        const uniqueDiseases = new Set<string>();
        const uniqueTargets = new Set<string>();
        const uniqueImagingModalities = new Set<string>();

        const normalizeDiseaseName = (diseaseName: string | undefined | null): string => {
            if (!diseaseName || typeof diseaseName !== 'string') {
                return 'unknown disease';
            }
            
            const normalized = diseaseName.toLowerCase()
                .replace(/['"]/g, '')
                .replace(/\s+/g, ' ')
                .trim();
            
            if (normalized.includes('alzheimer') || normalized.includes('ad')) {
                return 'alzheimer\'s disease';
            }
            if (normalized.includes('parkinson') || normalized.includes('pd')) {
                return 'parkinson\'s disease';
            }
            if (normalized.includes('diabetes') || normalized.includes('dm')) {
                return 'diabetes mellitus';
            }
            
            return normalized;
        };

        // Step 1: Create gene nodes
        genesData.forEach((gene) => {
            const geneNodeId = `gene_${gene.ensembl_id}`;
            nodeIdMap.set(gene.ensembl_id, geneNodeId);
            
            nodes.push({
                id: geneNodeId,
                label: gene.gene_symbol || gene.ensembl_id.substring(0, 8),
                group: 'gene',
                color: '#4CAF50',
                shape: 'circle',
                icon: '🧬',
                gene: gene
            });
        });

        // Step 2: Collect all unique drugs
        genesData.forEach((gene) => {
            if (gene.associated_drugs && Array.isArray(gene.associated_drugs)) {
                gene.associated_drugs.forEach((drug) => {
                    const drugKey = drug.chembl_id || drug.drug_name;
                    if (drugKey && typeof drugKey === 'string' && !uniqueDrugs.has(drugKey)) {
                        uniqueDrugs.add(drugKey);
                    }
                });
            }
        });

        // Step 3: Create drug nodes
        uniqueDrugs.forEach((drugKey) => {
            let drugInfo: DrugInfo | null = null;
            genesData.forEach((gene) => {
                if (gene.associated_drugs && Array.isArray(gene.associated_drugs)) {
                    const drug = gene.associated_drugs.find(d => 
                        (d.chembl_id || d.drug_name) === drugKey
                    );
                    if (drug) drugInfo = drug;
                }
            });

            if (drugInfo) {
                const drugNodeId = `drug_${drugKey}`;
                nodes.push({
                    id: drugNodeId,
                    label: (drugInfo as DrugInfo).drug_name || 'Unknown Drug',
                    group: 'drug',
                    color: '#2196F3',
                    shape: 'box',
                    icon: '💊',
                    drug: drugInfo
                });
            }
        });

        // Step 4: Collect all unique diseases
        genesData.forEach((gene) => {
            if (gene.associated_drugs && Array.isArray(gene.associated_drugs)) {
                gene.associated_drugs.forEach((drug) => {
                    if (drug.related_diseases && Array.isArray(drug.related_diseases)) {
                        drug.related_diseases.forEach((disease) => {
                            const normalizedName = normalizeDiseaseName(disease.disease_name);
                            if (!uniqueDiseases.has(normalizedName)) {
                                uniqueDiseases.add(normalizedName);
                            }
                        });
                    }
                });
            }
        });

        // Step 5: Create disease nodes
        uniqueDiseases.forEach((normalizedDiseaseName) => {
            const diseaseNodeId = `disease_${normalizedDiseaseName.replace(/\s+/g, '_')}`;
            nodes.push({
                id: diseaseNodeId,
                label: normalizedDiseaseName.split(' ').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' '),
                group: 'disease',
                color: '#FF9800',
                shape: 'circle',
                icon: '🦠'
            });
        });

        // Step 6: Collect all unique therapeutic targets
        genesData.forEach((gene) => {
            if (gene.associated_drugs && Array.isArray(gene.associated_drugs)) {
                gene.associated_drugs.forEach((drug) => {
                    if (drug.therapeutic_targets && Array.isArray(drug.therapeutic_targets)) {
                        drug.therapeutic_targets.forEach((target) => {
                            const targetKey = target.target_name;
                            if (targetKey && typeof targetKey === 'string' && !uniqueTargets.has(targetKey)) {
                                uniqueTargets.add(targetKey);
                            }
                        });
                    }
                });
            }
        });

        // Step 7: Create therapeutic target nodes
        uniqueTargets.forEach((targetName) => {
            const targetNodeId = `target_${targetName.replace(/\s+/g, '_')}`;
            nodes.push({
                id: targetNodeId,
                label: targetName,
                group: 'target',
                color: '#9C27B0',
                shape: 'circle',
                icon: '🎯'
            });
        });

        // Step 8: Collect all unique imaging modalities
        genesData.forEach((gene) => {
            if (gene.associated_drugs && Array.isArray(gene.associated_drugs)) {
                gene.associated_drugs.forEach((drug) => {
                    if (drug.related_diseases && Array.isArray(drug.related_diseases)) {
                        drug.related_diseases.forEach((disease) => {
                            if (disease.imaging_modalities && Array.isArray(disease.imaging_modalities)) {
                                disease.imaging_modalities.forEach((modality) => {
                                    if (modality && typeof modality === 'string' && !uniqueImagingModalities.has(modality)) {
                                        uniqueImagingModalities.add(modality);
                                    }
                                });
                            }
                        });
                    }
                });
            }
        });

        // Step 9: Create imaging modality nodes
        uniqueImagingModalities.forEach((modality) => {
            const imagingNodeId = `imaging_${modality.replace(/\s+/g, '_')}`;
            nodes.push({
                id: imagingNodeId,
                label: modality,
                group: 'imaging',
                color: '#FFEB3B',
                shape: 'diamond',
                icon: '📡'
            });
        });

        // Step 10: Create edges
        genesData.forEach((gene) => {
            const geneNodeId = `gene_${gene.ensembl_id}`;
            
            if (gene.associated_drugs && Array.isArray(gene.associated_drugs)) {
                gene.associated_drugs.forEach((drug) => {
                    const drugNodeId = `drug_${drug.chembl_id || drug.drug_name}`;
                    
                    edges.push({
                        from: geneNodeId,
                        to: drugNodeId,
                        color: '#333',
                        width: 3,
                        style: 'solid'
                    });

                    if (drug.therapeutic_targets && Array.isArray(drug.therapeutic_targets)) {
                        drug.therapeutic_targets.forEach((target) => {
                            if (target.target_name && typeof target.target_name === 'string') {
                                const targetNodeId = `target_${target.target_name.replace(/\s+/g, '_')}`;
                                edges.push({
                                    from: drugNodeId,
                                    to: targetNodeId,
                                    color: '#666',
                                    width: 2,
                                    style: 'dotted'
                                });
                            }
                        });
                    }

                    if (drug.related_diseases && Array.isArray(drug.related_diseases)) {
                        drug.related_diseases.forEach((disease) => {
                            const normalizedName = normalizeDiseaseName(disease.disease_name);
                            const diseaseNodeId = `disease_${normalizedName.replace(/\s+/g, '_')}`;
                            
                            edges.push({
                                from: drugNodeId,
                                to: diseaseNodeId,
                                color: '#666',
                                width: 2,
                                style: 'dashed'
                            });

                            if (disease.imaging_modalities && Array.isArray(disease.imaging_modalities)) {
                                disease.imaging_modalities.forEach((modality) => {
                                    if (modality && typeof modality === 'string') {
                                        const imagingNodeId = `imaging_${modality.replace(/\s+/g, '_')}`;
                                        edges.push({
                                            from: diseaseNodeId,
                                            to: imagingNodeId,
                                            color: '#999',
                                            width: 2,
                                            style: 'dash-dot'
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            }
        });

        return { nodes, edges };
    };

    const renderNetworkGraph = () => {
        const networkData = renderNetworkVisualization();
        if (!networkData) return null;

        return (
            <div style={{ 
                width: '100%', 
                height: '600px', 
                border: '1px solid #ddd', 
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <PlotlyNetworkVisualization 
                    nodes={networkData.nodes} 
                    edges={networkData.edges}
                    onNodeClick={handleGeneClick}
                />
            </div>
        );
    };

    // Check if feature is available for current user tier
    const isFeatureAvailable = (feature: string): boolean => {
        if (!isLoggedIn) return false;
        
        // For now, all logged-in users (including free tier) can use basic features
        // This can be expanded later for tier-specific features
        const freeTierFeatures = ['gene_search', 'paper_upload', 'basic_reasoning'];
        return freeTierFeatures.includes(feature);
    };

    if (loading) {
        return (
            <DashboardWrapper>
                <ContentView>
                    <ResponsiveContentContainer>
                        <BackButton onClick={() => navigate('/')}>
                            ← Back to Home
                        </BackButton>
                        <HeaderTitle>Gene Network Analysis</HeaderTitle>
                        <HeaderDescription>
                            Loading gene data and network connections...
                        </HeaderDescription>
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <div style={{ fontSize: '18px', color: Colors.theme.primary }}>
                                🔬 Analyzing gene networks...
                            </div>
                        </div>
                    </ResponsiveContentContainer>
                </ContentView>
                <Footer />
            </DashboardWrapper>
        );
    }

    // Show error only if it's a real error (not just login requirement)
    // Allow non-logged-in users to see the default gene results

    return (
        <DashboardWrapper>
            <ContentView>
                <ResponsiveContentContainer>
                    <BackButton onClick={() => navigate('/')}>
                        ← Back to Home
                    </BackButton>
                    
                    <HeaderTitle>Biomedical Discovery Platform</HeaderTitle>
                    <HeaderDescription>
                        {isLoggedIn 
                            ? `Explore genes, diseases, therapeutic targets, and medical imaging data. Current tier: ${userTier?.tier_name || 'Loading...'}`
                            : 'Explore gene networks, diseases, therapeutic targets, and medical imaging data. Log in to search genes and upload scientific papers.'
                        }
                    </HeaderDescription>

                    {/* Action Buttons Container */}
                    <ActionButtonsContainer>
                        <SearchContainer>
                            <SearchInput
                                type="text"
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                onKeyPress={handleSearchKeyPress}
                                placeholder={isLoggedIn 
                                    ? "Search by gene symbol (e.g., APP), Ensembl ID (e.g., ENSG00000142192), gene name, or description..."
                                    : "Log in to search genes..."
                                }
                                disabled={isSearching || !isLoggedIn}
                            />
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '15px' }}>
                                <SearchButton 
                                    onClick={handleSearch}
                                    disabled={isSearching || !searchQuery.trim() || !isLoggedIn}
                                >
                                    {isSearching ? 'Searching...' : '🔍 Search Genes'}
                                </SearchButton>
                                {searchQuery && (
                                    <SearchButton 
                                        onClick={resetSearch}
                                        style={{ 
                                            backgroundColor: '#6c757d'
                                        }}
                                    >
                                        Reset
                                    </SearchButton>
                                )}
                            </div>
                            {!isLoggedIn && (
                                <LoginPrompt style={{ marginTop: '15px', padding: '15px', fontSize: '14px' }}>
                                    🔒 Log in to search for genes and view reasoning chains
                                </LoginPrompt>
                            )}
                            <SearchHelpText>
                                💡 <strong>Example:</strong> Try searching for "APP" (Amyloid Precursor Protein), "ENSG00000142192" (APP Ensembl ID), or "amyloid" to find related genes.
                            </SearchHelpText>
                        </SearchContainer>

                        <div style={{ marginTop: '20px' }}>
                            <UploadButton 
                                onClick={handleOpenPaperUpload}
                                disabled={!isLoggedIn}
                            >
                                📄 Upload Scientific Paper
                            </UploadButton>
                            {!isLoggedIn && (
                                <LoginPrompt style={{ marginTop: '10px', fontSize: '13px', textAlign: 'center' }}>
                                    🔒 Log in to upload papers
                                </LoginPrompt>
                            )}
                        </div>
                    </ActionButtonsContainer>

                    {/* Paper Upload Modal */}
                    {showPaperUploadModal && (
                        <ModalOverlay onClick={handleClosePaperUpload}>
                            <ModalContent onClick={(e) => e.stopPropagation()}>
                                <ModalHeader>
                                    <h3>Upload Scientific Paper</h3>
                                    <ModalCloseButton onClick={handleClosePaperUpload}>×</ModalCloseButton>
                                </ModalHeader>
                                
                                <UploadTabs>
                                    <UploadTab 
                                        active={uploadTab === 'file'}
                                        onClick={() => setUploadTab('file')}
                                    >
                                        📁 Upload PDF
                                    </UploadTab>
                                    <UploadTab 
                                        active={uploadTab === 'url'}
                                        onClick={() => setUploadTab('url')}
                                    >
                                        🔗 Enter URL
                                    </UploadTab>
                                </UploadTabs>

                                <UploadTabContent>
                                    {uploadTab === 'file' ? (
                                        <FileUploadArea
                                            onClick={() => fileInputRef.current?.click()}
                                            hasFile={!!selectedFile}
                                        >
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".pdf"
                                                onChange={handleFileSelect}
                                                style={{ display: 'none' }}
                                            />
                                            {selectedFile ? (
                                                <div>
                                                    <p>✅ {selectedFile.name}</p>
                                                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                    </p>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p>📄 Click to select PDF file</p>
                                                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                        Maximum file size: 15MB
                                                    </p>
                                                </div>
                                            )}
                                        </FileUploadArea>
                                    ) : (
                                        <div>
                                            <URLInput
                                                type="text"
                                                value={paperURL}
                                                onChange={handleURLChange}
                                                placeholder="Enter paper URL (e.g., https://pubmed.ncbi.nlm.nih.gov/...)"
                                            />
                                            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                Enter a URL to a scientific paper (PubMed, arXiv, bioRxiv, etc.)
                                            </p>
                                        </div>
                                    )}

                                    {uploadError && (
                                        <div style={{ 
                                            marginTop: '15px', 
                                            padding: '10px', 
                                            backgroundColor: '#fee', 
                                            color: '#c33', 
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}>
                                            ❌ {uploadError}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                                        <SearchButton 
                                            onClick={handleClosePaperUpload}
                                            style={{ backgroundColor: '#6c757d' }}
                                        >
                                            Cancel
                                        </SearchButton>
                                        <SearchButton 
                                            onClick={handlePaperUpload}
                                            disabled={isUploading || (uploadTab === 'file' && !selectedFile) || (uploadTab === 'url' && !paperURL.trim())}
                                        >
                                            {isUploading ? 'Processing...' : 'Upload & Analyze'}
                                        </SearchButton>
                                    </div>
                                </UploadTabContent>
                            </ModalContent>
                        </ModalOverlay>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div style={{ 
                            marginTop: '20px', 
                            padding: '15px', 
                            backgroundColor: '#fee', 
                            color: '#c33', 
                            borderRadius: '8px',
                            border: '1px solid #fcc'
                        }}>
                            ❌ {error}
                        </div>
                    )}

                    {/* Network Visualization - Show for all users */}
                    <NetworkContainer>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '15px'
                        }}>
                            <h3 style={{ margin: 0, color: '#333' }}>Reasoning Graph</h3>
                            {isLoggedIn && (
                                <button
                                    onClick={handleRefresh}
                                    disabled={isRefreshing}
                                    style={{
                                        padding: '8px 12px',
                                        backgroundColor: isRefreshing ? '#ccc' : '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {isRefreshing ? (
                                        <>
                                            <span style={{ 
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                border: '2px solid #fff',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></span>
                                            Refreshing...
                                        </>
                                    ) : (
                                        <>
                                            🔄 Refresh Data
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                        {renderNetworkGraph()}
                        {!isLoggedIn && (
                            <div style={{ 
                                marginTop: '15px', 
                                padding: '15px', 
                                backgroundColor: '#fff3cd', 
                                borderRadius: '8px',
                                border: '1px solid #ffc107',
                                textAlign: 'center'
                            }}>
                                <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                                    🔒 <strong>Log in</strong> to search for specific genes and upload scientific papers for AI-powered analysis.
                                </p>
                            </div>
                        )}
                    </NetworkContainer>

                    {selectedGene && (
                        <div style={{
                            marginTop: '20px',
                            padding: '15px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '2px solid #FF6B6B',
                            maxWidth: '500px'
                        }}>
                            <h4>Selected Gene: {selectedGene.gene_symbol || selectedGene.ensembl_id}</h4>
                            <p><strong>Ensembl ID:</strong> {selectedGene.ensembl_id}</p>
                            <p><strong>Gene Name:</strong> {selectedGene.gene_name || 'Unknown'}</p>
                            <p><strong>Chromosome:</strong> {selectedGene.chromosome || 'Unknown'}</p>
                            <p><strong>Function:</strong> {selectedGene.gene_function || 'Unknown'}</p>
                            <p><strong>PDB Structures:</strong> {selectedGene.pdb_ids.length}</p>
                            <p><strong>Associated Drugs:</strong> {selectedGene.associated_drugs.length}</p>
                            {selectedGene.pdb_ids.length > 0 && (
                                <button
                                    onClick={() => handleGeneClick(selectedGene)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#FF6B6B',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        marginTop: '10px'
                                    }}
                                >
                                    View 3D Structure
                                </button>
                            )}
                            <button
                                onClick={() => setSelectedGene(null)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginTop: '10px',
                                    marginLeft: '10px'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    )}
                </ResponsiveContentContainer>
            </ContentView>
            <Footer />
        </DashboardWrapper>
    );
};

export default Gene;
